/**
 * "Tu talla" — the shopper's size on collection / search pages.
 *
 * Bar markup:  snippets/my-size-bar.liquid   ([data-my-size-bar], size pills)
 * Card markup: snippets/card-product.liquid  (.card-sizes[data-sizes])
 *
 * Behaviour
 *  - No size chosen: every card shows "● En stock" and one row of in-stock
 *    size chips (swipes sideways) as rendered by Liquid.
 *  - Size chosen by the shopper (tap a pill; tap again to clear):
 *      · card has it   → the matching chip turns green and the row scrolls to
 *        it; tapping the green chip adds that variant to the cart directly
 *      · card lacks it → the block is replaced by "42 agotada · Avísame"
 *        (link to the PDP with that sold-out variant + notify=1, which opens
 *        the back-in-stock modal) or "42 no disponible" when the product never
 *        had that size; the card is dimmed.
 *      · other kind of product (clothing while a shoe size is chosen, and
 *        vice versa) → hidden.
 *    Cards that have the size are moved before the ones that do not.
 *  - "Solo mi talla" also hides the cards that do not have the size.
 *  - Nothing is remembered (merchant's decision 2026-09): every page load
 *    starts with no size and results only appear after the shopper picks one.
 *  - Sizes are merged into groups (SIZE_GROUPS): choosing 37.5 matches
 *    37, 37⅓, 37.5 and 37⅔; the card shows the product's real size.
 *  - Re-applies after facets.js swaps #ProductGridContainer (filter/sort/
 *    pagination) via a MutationObserver that only reacts to new cards.
 *  - Only acts on the main grid (#ProductGridContainer), not on carousels.
 */
(function () {
  'use strict';

  // Current choice, kept in memory only (lost on reload / next page on purpose)
  var state = { size: '', only: false };
  var reordering = false; // true while reorder() moves cards (ignore those mutations)

  // "42⅔", "42 2/3", "42.6", "42,5 EU" → comparable key
  function norm(s) {
    return String(s || '')
      .trim()
      .toLowerCase()
      .replace(/\s*eu$/, '')
      .replace(',', '.')
      .replace(/\s*1\/3/, '.33').replace('⅓', '.33')
      .replace(/\s*2\/3/, '.67').replace('⅔', '.67')
      .replace('½', '.5')
      .replace(/\.3$/, '.33').replace(/\.6$/, '.67')
      .replace(/\.0$/, '');
  }
  // "42", "42.5", "37.33" → shoe size; "2XS", "M", "XL" → clothing
  function isNumericSize(s) { return /^\d+(\.\d+)?$/.test(norm(s)); }

  // Size groups shown to the shopper as ONE value (2026-09, merchant's table).
  // Key = norm() of each real size, value = the label shown in "Tu talla".
  // Sizes not listed here stay as their own option (36, 40, 42, 44, 45 ...).
  var SIZE_GROUPS = {
    '36.5': '36.5',  '36.67': '36.5',
    '37': '37.5',    '37.33': '37.5',  '37.5': '37.5',  '37.67': '37.5',
    '38': '38',      '38.5': '38.5',   '38.67': '38.5',
    '39': '39',      '39.33': '39',
    '40.5': '40.5',  '40.67': '40.5',
    '41': '41',      '41.33': '41',    '41.5': '41',
    '42.5': '42.5',  '42.67': '42.5',
    '43': '43',      '43.33': '43',    '43.5': '43',
    '44.5': '44.5',  '44.67': '44.5',
    '45.33': '45.5', '45.5': '45.5',
    '46.5': '46.5',  '46.67': '46.5',
    '47.33': '47.5', '47.5': '47.5'
  };
  // Grouping is only for MIXED-brand collections (Novedades, Rebajas …):
  // each brand has its own EU scale, so in a single-brand collection
  // (Adidas, New Balance, a model sub-collection) the real sizes are
  // offered as they are. Decided per page from the ?view=sizes answer
  // (2026-09, merchant decision).
  var useGroups = true;
  // Label a real size maps to ("37⅓" -> "37.5", "40" -> "40", "M" -> "M")
  function groupOf(s) {
    var k = norm(s);
    return (useGroups && SIZE_GROUPS[k]) || String(s || '').trim();
  }
  // Comparison key: "40 EU", "40,5" and "40.5" all match the pill "40.5"
  function keyOf(s) {
    var k = norm(s);
    return (useGroups && SIZE_GROUPS[k]) || k;
  }
  function sortKey(label) {
    var n = parseFloat(norm(label));
    return isNaN(n) ? -1 : n;
  }

  function itemOf(el) {
    return el.closest('.grid__item') || el.closest('card-product-cs') || el.parentElement;
  }

  // data-variants="36:123:1|37:124:0" → [{size, id, available}] (every size,
  // sold out included). Split from the right so a ':' inside a size is safe.
  function parseVariants(box) {
    return (box.dataset.variants || '').split('|').filter(Boolean).map(function (entry) {
      var parts = entry.split(':');
      if (parts.length < 3) return null;
      return {
        available: parts.pop() === '1',
        id: parts.pop(),
        size: parts.join(':')
      };
    }).filter(Boolean);
  }

  // Hide the "›" hint once the row is scrolled to its end (or never overflows)
  function updateHint(row) {
    var scroller = row.parentElement;
    if (!scroller) return;
    var atEnd = row.scrollLeft + row.clientWidth >= row.scrollWidth - 2;
    scroller.classList.toggle('card-sizes__scroller--end', atEnd);
  }

  // Only the main product grid (not carousels elsewhere on the page)
  function gridRoot() {
    return document.getElementById('ProductGridContainer') || document.getElementById('product-grid') || document;
  }
  function gridBoxes() {
    return Array.prototype.slice.call(gridRoot().querySelectorAll('.card-sizes'));
  }

  function apply(bar) {
    var mySize = state.size;
    var only = state.only;
    var myGroup = keyOf(mySize);
    var myNumeric = isNumericSize(mySize);
    var t = bar.dataset;

    var onlyWrap = bar.querySelector('[data-my-size-only-wrap]');
    if (onlyWrap) onlyWrap.hidden = !mySize;

    gridBoxes().forEach(function (box) {
      var block = box.querySelector('.card-sizes__block');
      var row = box.querySelector('.card-sizes__row');
      var my = box.querySelector('.card-sizes__my');
      var item = itemOf(box);
      var sizes = (box.dataset.sizes || '').split('|').filter(Boolean);

      // reset
      item.classList.remove('my-size--no', 'my-size--other', 'my-size--hidden');
      if (block) block.hidden = false;
      if (row) {
        row.querySelectorAll('.card-sizes__chip--hl').forEach(function (c) {
          c.classList.remove('card-sizes__chip--hl');
          c.removeAttribute('aria-label');
        });
        if (!row.dataset.hint) {
          row.dataset.hint = '1';
          row.addEventListener('scroll', function () { updateHint(row); }, { passive: true });
        }
      }
      if (my) { my.hidden = true; my.innerHTML = ''; my.className = 'card-sizes__my'; }

      if (!mySize || !my) {
        if (row) { row.scrollLeft = 0; updateHint(row); }
        return;
      }

      // Mixed collections: a shoe size says nothing about a hoodie, so
      // clothing is hidden while a shoe size is chosen (and vice versa).
      // The kind is read from every variant, sold out included.
      var allVariants = parseVariants(box);
      var kindSample = allVariants.length ? allVariants[0].size : sizes[0];
      if (kindSample !== undefined && isNumericSize(kindSample) !== myNumeric) {
        item.classList.add('my-size--other', 'my-size--hidden');
        return;
      }

      // every real size of this product that belongs to the chosen group,
      // e.g. chosen 37.5 -> this card has 37⅓ in stock
      var hits = sizes.filter(function (s) { return keyOf(s) === myGroup; });

      if (hits.length > 0 && row) {
        // in stock → the matching chip turns green (tap = straight to cart,
        // see addToCart) and the row scrolls to it
        var first = null;
        row.querySelectorAll('.card-sizes__chip').forEach(function (chip) {
          if (keyOf(chip.dataset.size) !== myGroup) return;
          chip.classList.add('card-sizes__chip--hl');
          chip.setAttribute('aria-label', chip.dataset.size + ' · ' + (t.tAdd || ''));
          if (!first) first = chip;
        });
        row.scrollLeft = first ? Math.max(0, first.offsetLeft - 6) : 0;
        updateHint(row);
        return;
      }

      // not in stock: the row collapses to a grey bar with Avísame
      if (block) block.hidden = true;
      my.hidden = false;
      my.classList.add('card-sizes__my--no');

      // a sold-out variant of the chosen size → "agotada · Avísame"
      var soldOut = parseVariants(box).filter(function (v) {
        return !v.available && keyOf(v.size) === myGroup;
      })[0];

      var msg = document.createElement('span');
      if (soldOut && box.dataset.url && /^\d+$/.test(soldOut.id)) {
        msg.textContent = soldOut.size + ' ' + t.tSoldout;
        my.appendChild(msg);
        var a = document.createElement('a');
        a.href = box.dataset.url + (box.dataset.url.indexOf('?') === -1 ? '?' : '&') + 'variant=' + soldOut.id + '&notify=1';
        a.className = 'card-sizes__notify';
        a.textContent = t.tNotify;
        my.appendChild(a);
      } else {
        // the product never had this size: nothing to be notified about
        msg.textContent = mySize + ' ' + (t.tUnavailable || t.tSoldout);
        my.appendChild(msg);
      }
      item.classList.add('my-size--no');
      if (only) item.classList.add('my-size--hidden');
    });

    reorder(!!mySize);

    // moving cards in the DOM (reorder) resets their scroll position:
    // bring the green chip into view afterwards
    gridBoxes().forEach(function (box) {
      var row = box.querySelector('.card-sizes__row');
      if (!row) return;
      var first = row.querySelector('.card-sizes__chip--hl');
      row.scrollLeft = first ? Math.max(0, first.offsetLeft - 6) : 0;
      updateHint(row);
    });
  }

  // Size chosen → products that have it first, the rest after, each part in
  // the original order. No size → original order. Only touches the DOM when
  // the order really changes, so the MutationObserver does not re-trigger.
  function reorder(sizeChosen) {
    var firstBox = gridBoxes()[0];
    if (!firstBox) return;
    var grid = itemOf(firstBox).parentElement;
    if (!grid) return;
    var items = Array.prototype.slice.call(grid.children);
    items.forEach(function (el, i) {
      if (!el.dataset.myOrder) el.dataset.myOrder = String(i + 1);
    });
    function rank(el) {
      if (el.classList.contains('my-size--other')) return 2; // hidden: other kind of product
      if (el.classList.contains('my-size--no')) return 1;    // no stock in the size
      return 0;                                              // has the size
    }
    var sorted = items.slice().sort(function (a, b) {
      if (sizeChosen && rank(a) !== rank(b)) return rank(a) - rank(b);
      return Number(a.dataset.myOrder) - Number(b.dataset.myOrder);
    });
    var changed = sorted.some(function (el, i) { return el !== items[i]; });
    if (!changed) return;
    reordering = true;
    sorted.forEach(function (el) { grid.appendChild(el); });
    // MutationObserver callbacks are async: clear the flag after they ran
    setTimeout(function () { reordering = false; }, 0);
  }

  // Same follow-up as assets/product-form.js after an add to cart:
  // team-store / regular mixed cart guard and the recommendations slider.
  function afterCartAdd() {
    var team = false, regular = false;
    document.querySelectorAll('.cart-item').forEach(function (item) {
      var v = item.getAttribute('data-team-store-drawer');
      if (v === 'true') team = true;
      else if (v === 'false') regular = true;
    });
    if (team && regular) {
      var checkout = document.getElementById('CartDrawer-Checkout');
      var popup = document.getElementById('drawer-popup');
      if (checkout) checkout.setAttribute('disabled', 'disabled');
      if (popup) popup.style.display = 'block';
    }
    if (typeof Swiper === 'function' && document.getElementById('product-slider-cart-drawer')) {
      try {
        new Swiper('#product-slider-cart-drawer', {
          navigation: {
            nextEl: '.section-recommend-swiper-button-next',
            prevEl: '.section-recommend-swiper-button-prev'
          },
          slidesPerView: 2,
          spaceBetween: 4
        });
      } catch (e) {}
    }
  }

  // Green chip tapped: add one unit straight to the cart and open the cart
  // drawer, the same way the theme's product form does (assets/product-form.js).
  // Returns false when that is not possible, so the tap follows the link instead.
  function addToCart(chip) {
    var variantId = chip.dataset.variant || '';
    var cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    if (!/^\d+$/.test(variantId) || !cart || typeof cart.renderContents !== 'function' ||
        typeof fetchConfig !== 'function' || !window.routes || !window.routes.cart_add_url) {
      return false;
    }
    if (chip.classList.contains('card-sizes__chip--loading')) return true;

    var config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];
    var fd = new FormData();
    fd.append('id', variantId);
    fd.append('quantity', '1');
    if (cart.getSectionsToRender) {
      fd.append('sections', cart.getSectionsToRender().map(function (s) { return s.id; }));
      fd.append('sections_url', window.location.pathname);
    }
    if (cart.setActiveElement) cart.setActiveElement(chip);
    config.body = fd;

    chip.classList.add('card-sizes__chip--loading');
    var fallback = function () { window.location.href = chip.href; };
    fetch(window.routes.cart_add_url, config)
      .then(function (r) { return r.json(); })
      .then(function (res) {
        chip.classList.remove('card-sizes__chip--loading');
        if (res.status) { fallback(); return; } // e.g. just sold out → PDP shows it
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', productVariantId: variantId });
        }
        cart.renderContents(res);
        // first item in an empty cart: product-form.js does the same, the
        // drawer keeps `.is-empty` otherwise and hides the line just added
        if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
        afterCartAdd();
      })
      .catch(function () {
        chip.classList.remove('card-sizes__chip--loading');
        fallback();
      });
    return true;
  }

  function init() {
    var bar = document.querySelector('[data-my-size-bar]');
    if (!bar) return;

    var pillsWrap = bar.querySelector('[data-my-size-pills]');
    var onlyInput = bar.querySelector('[data-my-size-only]');
    var pills = [];

    // The filter's raw size values (printed by Liquid) → pills. Grouped
    // labels when useGroups (mixed brands), the real sizes otherwise;
    // clothing / text sizes keep their order, numeric sizes sorted ascending,
    // shoe sizes first, clothing (S/M/L) after. Rebuilt once the
    // ?view=sizes answer says whether this is a single-brand collection.
    var rawValues = [];
    if (pillsWrap) {
      [].forEach.call(pillsWrap.querySelectorAll('[data-my-size-pill]'), function (b) {
        rawValues.push(b.dataset.value);
        b.remove();
      });
    }
    function buildPills() {
      if (!pillsWrap) return;
      pills.forEach(function (b) { b.remove(); });
      pills = [];
      var seen = {}, textOpts = [], numOpts = [];
      rawValues.forEach(function (v) {
        var label = groupOf(v);
        if (!label || seen[label] || /^default title$/i.test(label)) return;
        seen[label] = true;
        (isNumericSize(label) ? numOpts : textOpts).push(label);
      });
      numOpts.sort(function (a, b) { return sortKey(a) - sortKey(b); });
      numOpts.concat(textOpts).forEach(function (label) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'my-size-bar__pill';
        b.setAttribute('data-my-size-pill', '');
        b.setAttribute('aria-pressed', 'false');
        b.dataset.value = label;
        b.textContent = label;
        pillsWrap.appendChild(b);
        pills.push(b);
      });
    }
    buildPills();

    // Only offer sizes that give results: ask the ?view=sizes template
    // (templates/collection.sizes.liquid / search.sizes.liquid) which sizes
    // have at least one in-stock variant here (current filters included) and
    // drop the other pills. The row stays hidden (max 2.5 s) until known;
    // on any error every pill is kept.
    if (pillsWrap && pills.length) {
      var shown = false;
      var show = function () {
        if (shown) return;
        shown = true;
        bar.classList.remove('my-size-bar--loading');
        updatePillsHint();
      };
      bar.classList.add('my-size-bar--loading');
      setTimeout(show, 2500);
      var base = new URL(window.location.href);
      base.searchParams.delete('page');
      // an applied size filter would leave only that size: ignore it here
      Array.from(base.searchParams.keys()).forEach(function (k) {
        if (/talla|size/i.test(k)) base.searchParams.delete(k);
      });
      base.searchParams.set('view', 'sizes');
      var getPage = function (n) {
        var u = new URL(base.href);
        if (n > 1) u.searchParams.set('page', n);
        return fetch(u.href, { credentials: 'same-origin' })
          .then(function (r) { if (!r.ok) throw new Error(r.status); return r.text(); })
          .then(function (t) { return JSON.parse(t.trim()); });
      };
      getPage(1)
        .then(function (first) {
          var pages = Math.min(parseInt(first.pages, 10) || 1, 8); // cap: 8 × 250 products
          var rest = [];
          for (var n = 2; n <= pages; n++) rest.push(getPage(n));
          return Promise.all(rest).then(function (more) { return [first].concat(more); });
        })
        .then(function (all) {
          // one brand in the whole collection → real sizes, no grouping
          var vendors = {};
          all.forEach(function (d) {
            String(d.vendors || '').split('|').forEach(function (v) { if (v) vendors[v] = true; });
          });
          var vendorCount = Object.keys(vendors).length;
          if (vendorCount === 1 && useGroups) {
            useGroups = false;
            buildPills();
          }
          var ok = {};
          var hasShoes = false;
          all.forEach(function (d) {
            if (d.shoes === true) hasShoes = true;
            String(d.sizes || '').split('|').forEach(function (s) { if (s) ok[keyOf(s)] = true; });
          });
          if (!Object.keys(ok).length) return; // nothing in stock / unexpected: keep all pills
          pills = pills.filter(function (b) {
            if (ok[keyOf(b.dataset.value)]) return true;
            b.remove();
            return false;
          });
          if (!pills.length) { bar.hidden = true; return; }
          // clothing-only collection: S/M/L first, trouser waists (28, 30 …) after
          if (!hasShoes) {
            var letters = pills.filter(function (b) { return !isNumericSize(b.dataset.value); });
            var numbers = pills.filter(function (b) { return isNumericSize(b.dataset.value); });
            if (letters.length && numbers.length) {
              pills = letters.concat(numbers);
              pills.forEach(function (b) { pillsWrap.appendChild(b); });
            }
          }
        })
        .catch(function () {})
        .then(show);
    }

    // "›" at the end of the pills row: scroll it on; hide when at the end
    var pillsMore = bar.querySelector('[data-my-size-more]');
    function updatePillsHint() {
      if (!pillsWrap || !pillsMore) return;
      var atEnd = pillsWrap.scrollLeft + pillsWrap.clientWidth >= pillsWrap.scrollWidth - 2;
      pillsWrap.parentElement.classList.toggle('my-size-bar__scroller--end', atEnd);
    }
    if (pillsWrap && pillsMore) {
      pillsMore.addEventListener('click', function () {
        pillsWrap.scrollBy({ left: Math.round(pillsWrap.clientWidth * 0.7), behavior: 'smooth' });
      });
      pillsWrap.addEventListener('scroll', updatePillsHint, { passive: true });
      window.addEventListener('resize', updatePillsHint);
      window.addEventListener('load', updatePillsHint);
      updatePillsHint();
    }

    function paintPills() {
      pills.forEach(function (b) {
        var on = b.dataset.value === state.size;
        b.classList.toggle('my-size-bar__pill--on', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
    }

    // Always start empty (nothing is remembered, also not on back/forward)
    function reset() {
      state.size = ''; state.only = false;
      if (onlyInput) onlyInput.checked = false;
      paintPills();
      apply(bar);
    }
    // delegated: pills may be rebuilt after the ?view=sizes answer
    if (pillsWrap) {
      pillsWrap.addEventListener('click', function (e) {
        var b = e.target.closest('[data-my-size-pill]');
        if (!b) return;
        // tap again to clear
        state.size = state.size === b.dataset.value ? '' : b.dataset.value;
        if (!state.size) { state.only = false; if (onlyInput) onlyInput.checked = false; }
        paintPills();
        apply(bar);
      });
    }
    if (onlyInput) {
      onlyInput.setAttribute('autocomplete', 'off');
      onlyInput.checked = false;
      onlyInput.addEventListener('change', function () {
        state.only = onlyInput.checked;
        apply(bar);
      });
    }
    window.addEventListener('pageshow', function (e) { if (e.persisted) reset(); });

    apply(bar);

    document.addEventListener('click', function (e) {
      if (!e.target.closest) return;
      // "›" button at the end of a row: advance the row by most of its width
      var more = e.target.closest('.card-sizes__more');
      if (more) {
        e.preventDefault();
        var row = more.parentElement && more.parentElement.querySelector('.card-sizes__row');
        if (row) row.scrollBy({ left: Math.round(row.clientWidth * 0.8), behavior: 'smooth' });
        return;
      }
      // green chip (the shopper's size): straight to the cart
      var hl = e.target.closest('.card-sizes__chip--hl');
      if (hl && addToCart(hl)) e.preventDefault();
    });

    // chip widths can change once web fonts load or the viewport is resized
    function refreshHints() {
      document.querySelectorAll('.card-sizes__row').forEach(updateHint);
    }
    var hintTimer;
    window.addEventListener('load', refreshHints);
    window.addEventListener('resize', function () {
      clearTimeout(hintTimer);
      hintTimer = setTimeout(refreshHints, 150);
    });

    // facets.js replaces the grid's innerHTML on filter / sort / page change
    var grid = document.getElementById('ProductGridContainer') || document.getElementById('product-grid');
    if (grid) {
      var timer;
      new MutationObserver(function (records) {
        // Re-apply only when new product cards arrived (facets.js replaced
        // the grid). Countdown timers, image sliders and our own edits also
        // mutate the grid and must not reset the size rows.
        if (reordering) return;
        var newCards = records.some(function (r) {
          return Array.prototype.some.call(r.addedNodes, function (n) {
            return n.nodeType === 1 && (
              (n.classList && n.classList.contains('card-sizes')) ||
              (n.querySelector && n.querySelector('.card-sizes'))
            );
          });
        });
        if (!newCards) return;
        clearTimeout(timer);
        timer = setTimeout(function () { apply(bar); }, 80);
      }).observe(grid, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
